import { Link } from "react-router-dom";
import { useLocalization } from "../../hooks/useLocalization";
import { useLocalePath } from "../../hooks/useLocalePath";
import { getMatchTypeLocalized } from "../../services/utils/utils";
import { formatMatchDate } from "../../utils/formatMatchDate";
import type { MatchScheduleItemData, MatchScheduleMode } from "../../types/match-schedule";
import { CommonTwitchLink } from "./CommonTwitchLink";
import { TeamLogo } from "./TeamLogo";

const hasStreamLink = (link?: string) => {
  const trimmed = link?.trim() ?? "";
  return trimmed.length > 0 && !/^tbd$/i.test(trimmed);
};

interface MatchScheduleItemProps {
  match: MatchScheduleItemData;
  mode: MatchScheduleMode;
  hideMatchType?: boolean;
  showStreamLink?: boolean;
  showDetailsLink?: boolean;
}

export const MatchScheduleItem = ({
  match,
  mode,
  hideMatchType,
  showStreamLink,
  showDetailsLink,
}: MatchScheduleItemProps) => {
  const { localized } = useLocalePath();
  const { strings, locale } = useLocalization();
  const when = formatMatchDate(match.time, locale, strings);
  const teamLogoAlt = (teamName: string) =>
    strings.COMMON.TEAM_LOGO_ALT.replace("{team}", teamName);
  const detailsEnabled = showDetailsLink ?? mode === "played";
  const streamEnabled = showStreamLink ?? mode === "upcoming";
  const streamReady = streamEnabled && mode === "upcoming" && hasStreamLink(match.link);
  const streamPending = streamEnabled && mode === "upcoming" && !hasStreamLink(match.link);

  return (
    <li className="match-card">
      <div className="flex w-full shrink-0 flex-col items-center gap-[0.85rem]">
        {!hideMatchType && (
          <p className="m-0 font-shentox text-[clamp(0.9rem,1.3vw,1.1rem)] tracking-[0.05em] uppercase">
            {getMatchTypeLocalized(match.matchType, strings)}
          </p>
        )}
        {when && (
          <time
            className="m-0 flex flex-col items-center gap-[0.3rem] font-jetbrains leading-[1.3]"
            dateTime={when.dateTime}
          >
            {mode === "upcoming" ? (
              <>
                <span className="font-shentox text-[clamp(0.85rem,1.2vw,1rem)] tracking-[0.06em] uppercase opacity-90">
                  {when.weekday}
                </span>
                <span className="text-[clamp(1rem,1.4vw,1.15rem)] font-medium text-light-blue">
                  {when.date}
                </span>
                <span className="text-[clamp(0.9rem,1.2vw,1rem)] opacity-90">{when.time}</span>
              </>
            ) : (
              <span className="text-[clamp(1rem,1.4vw,1.15rem)] font-medium text-light-blue">
                {when.date}
              </span>
            )}
          </time>
        )}
      </div>

      <div className="flex w-full min-w-0 flex-1 flex-col items-center justify-center">
        <div className="flex w-full min-w-0 items-center justify-center gap-4 max-md:gap-[0.65rem]">
          <div className="flex min-w-0 flex-1 flex-col items-center gap-[0.4rem]">
            <TeamLogo
              teamId={match.teamOne ?? "TBD"}
              alt={teamLogoAlt(match.teamOneName ?? strings.COMMON.TBD)}
              className="h-[clamp(3.25rem,5vw,4.5rem)] w-[clamp(3.25rem,5vw,4.5rem)] object-contain max-md:h-[clamp(2.75rem,12vw,3.5rem)] max-md:w-[clamp(2.75rem,12vw,3.5rem)]"
            />
            <span className="w-full font-jetbrains text-[clamp(0.8rem,1.1vw,0.9rem)] leading-[1.2] [overflow-wrap:anywhere] max-md:text-[clamp(0.75rem,3.2vw,0.85rem)]">
              {match.teamOneName ?? strings.COMMON.TBD}
            </span>
          </div>
          {mode === "upcoming" ? (
            <span className="inline-flex w-[4.5rem] shrink-0 items-center justify-center font-shentox text-[clamp(1rem,1.4vw,1.2rem)] tracking-[0.08em] opacity-70">
              {strings.COMMON.VS}
            </span>
          ) : (
            <span className="inline-flex w-[4.5rem] shrink-0 items-center justify-center font-jetbrains text-[clamp(1.25rem,2vw,1.6rem)] font-medium tracking-[0.04em] text-light-blue tabular-nums whitespace-nowrap leading-none">
              {match.teamOneScore} – {match.teamTwoScore}
            </span>
          )}
          <div className="flex min-w-0 flex-1 flex-col items-center gap-[0.4rem]">
            <TeamLogo
              teamId={match.teamTwo ?? "TBD"}
              alt={teamLogoAlt(match.teamTwoName ?? strings.COMMON.TBD)}
              className="h-[clamp(3.25rem,5vw,4.5rem)] w-[clamp(3.25rem,5vw,4.5rem)] object-contain max-md:h-[clamp(2.75rem,12vw,3.5rem)] max-md:w-[clamp(2.75rem,12vw,3.5rem)]"
            />
            <span className="w-full font-jetbrains text-[clamp(0.8rem,1.1vw,0.9rem)] leading-[1.2] [overflow-wrap:anywhere] max-md:text-[clamp(0.75rem,3.2vw,0.85rem)]">
              {match.teamTwoName ?? strings.COMMON.TBD}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-auto flex min-h-11 w-full min-w-0 shrink-0 flex-col items-center justify-end gap-[0.45rem] pt-[0.35rem]">
        {streamReady && (
          <CommonTwitchLink href={match.link ?? ""} label={strings.SCHEDULE.WATCH_STREAM} />
        )}
        {streamPending && (
          <p className="m-0 max-w-full text-center font-jetbrains text-[clamp(0.75rem,1.05vw,0.85rem)] leading-[1.4] text-inherit opacity-65 [overflow-wrap:anywhere]">
            {strings.SCHEDULE.STREAM_TBD}
          </p>
        )}
        {mode === "played" && match.youtubeLink && (
          <a
            href={match.youtubeLink}
            className="match-vod-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {strings.SCHEDULE.WATCH_VOD}
          </a>
        )}
        {detailsEnabled && (
          <Link to={localized(`/matches/${String(match.id)}`)} className="match-details-link">
            {strings.SCHEDULE.VIEW_MATCH_DETAILS}
          </Link>
        )}
      </div>
    </li>
  );
};

interface MatchScheduleListProps {
  matches: MatchScheduleItemData[];
  mode: MatchScheduleMode;
  className?: string;
  hideMatchType?: boolean;
  showStreamLink?: boolean;
  showDetailsLink?: boolean;
}

export const MatchScheduleList = ({
  matches,
  mode,
  className,
  hideMatchType,
  showStreamLink,
  showDetailsLink,
}: MatchScheduleListProps) => {
  if (!matches.length) {
    return null;
  }

  return (
    <ul className={className ?? "schedule-list"}>
      {matches.map((match) => (
        <MatchScheduleItem
          key={match.id}
          match={match}
          mode={mode}
          hideMatchType={hideMatchType}
          showStreamLink={showStreamLink}
          showDetailsLink={showDetailsLink}
        />
      ))}
    </ul>
  );
};
